import React from 'react';
import { useNavigate } from 'react-router-dom';
import { HelpCircle, Book, MessageSquare, Phone, Mail, ArrowLeft, ExternalLink } from 'lucide-react';

const HelpCenter: React.FC = () => {
  const navigate = useNavigate();

  const faqs = [
    {
      question: "How do I upload a contract for analysis?",
      answer: "Navigate to the Contract Review section, click 'Analysis Upload', and select your contract file (PDF, DOCX, or JSON format). The system will automatically analyze the document."
    },
    {
      question: "What is the Master Data section for?",
      answer: "Master Data is an administrative area where you can manage suppliers, categories, and contract types. Only admin users have access to this section."
    },
    {
      question: "How do I review contract clauses?",
      answer: "After uploading a contract, go to the 'Enhanced Clause Review' tab. You can mark each clause as Agreed, Rejected, Compromise, or Pending, and add comments for negotiation."
    },
    {
      question: "Can I export the negotiation results?",
      answer: "Yes! In the Negotiation Dashboard, you can download clause statuses as .txt files and generate negotiation emails based on your review."
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/')}
                className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
              >
                <ArrowLeft className="w-5 h-5" />
                Back to Dashboard
              </button>
              <h1 className="text-2xl font-bold text-gray-900">Help Center</h1>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Quick Links */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow p-6 hover:shadow-md transition-shadow cursor-pointer">
            <div className="flex items-center gap-3 mb-3">
              <Book className="w-8 h-8 text-blue-600" />
              <h3 className="text-lg font-semibold">Documentation</h3>
            </div>
            <p className="text-gray-600">Browse our comprehensive guides and tutorials</p>
          </div>
          <div className="bg-white rounded-lg shadow p-6 hover:shadow-md transition-shadow cursor-pointer">
            <div className="flex items-center gap-3 mb-3">
              <MessageSquare className="w-8 h-8 text-green-600" />
              <h3 className="text-lg font-semibold">Contact Support</h3>
            </div>
            <p className="text-gray-600">Get help from our support team</p>
          </div>
          <div className="bg-white rounded-lg shadow p-6 hover:shadow-md transition-shadow cursor-pointer">
            <div className="flex items-center gap-3 mb-3">
              <Phone className="w-8 h-8 text-purple-600" />
              <h3 className="text-lg font-semibold">Schedule a Call</h3>
            </div>
            <p className="text-gray-600">Book a demo or training session</p>
          </div>
        </div>

        {/* FAQs */}
        <div className="bg-white rounded-lg shadow">
          <div className="p-6 border-b">
            <h2 className="text-xl font-semibold text-gray-900">Frequently Asked Questions</h2>
          </div>
          <div className="divide-y">
            {faqs.map((faq, index) => (
              <div key={index} className="p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-2 flex items-start gap-2">
                  <HelpCircle className="w-5 h-5 text-blue-600 mt-0.5" />
                  {faq.question}
                </h3>
                <p className="text-gray-600 ml-7">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Contact Information */}
        <div className="mt-8 bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Contact Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-center gap-3">
              <Mail className="w-5 h-5 text-gray-600" />
              <div>
                <p className="text-sm text-gray-500">Email Support</p>
                <a href="mailto:support@contractmanager.com" className="text-blue-600 hover:underline">
                  support@contractmanager.com
                </a>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Phone className="w-5 h-5 text-gray-600" />
              <div>
                <p className="text-sm text-gray-500">Phone Support</p>
                <a href="tel:+1234567890" className="text-blue-600 hover:underline">
                  +1 (234) 567-890
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Resources */}
        <div className="mt-8 bg-blue-50 rounded-lg p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-3">Additional Resources</h2>
          <div className="space-y-2">
            <button className="flex items-center gap-2 text-blue-600 hover:underline">
              <ExternalLink className="w-4 h-4" />
              User Manual (PDF)
            </button>
            <button className="flex items-center gap-2 text-blue-600 hover:underline">
              <ExternalLink className="w-4 h-4" />
              Video Tutorials
            </button>
            <button className="flex items-center gap-2 text-blue-600 hover:underline">
              <ExternalLink className="w-4 h-4" />
              API Documentation
            </button>
          </div>
        </div>
      </main>
    </div>
  );
};

export default HelpCenter;